import html from "./data-view.html?raw"
import styles from "./data-view.css?inline"
import { sharedStyles } from "../../shared-styles"
import { graphStore } from "../../graph-store"
import "../dataset-chooser"
import "../results-table"
import type { DatasetChangeEvent, DatasetChooser } from "../dataset-chooser"
import type { ResultsTable } from "../results-table"
import { cursorRowSource } from "../results-table/cursor-source"
import { quadsToSelectResults } from "../sparql-view/quads-to-bindings"
import { FACETS_CHANNEL, type FacetSelectionMsg } from "../../facets-channel"
import * as persistedData from "../../persisted-data"
import "../launcher-button"

const ALL_TRIPLES_QUERY =
  "SELECT ?subject ?predicate ?object WHERE { ?subject ?predicate ?object }"

class DataView extends HTMLElement {
  private shadow: ShadowRoot
  private channel: BroadcastChannel
  private resultsTable!: ResultsTable
  private filterStatus!: HTMLDivElement
  private errorStatus!: HTMLDivElement
  private datasetChooser!: DatasetChooser
  // Async refresh races: store-cleared / store-updated / facet-broadcast can
  // overlap. Aborting the previous controller rejects the superseded chain's
  // graph-store calls with AbortError so the newest call's writes win.
  private controller: AbortController | null = null
  // The open windowed cursor backing the all-triples table, if any. Closed when
  // a new load supersedes it, on store-cleared, and on disconnect, so the
  // worker-side cursor is freed rather than leaked.
  private cursor: Awaited<
    ReturnType<typeof graphStore.openSelectCursor>
  > | null = null

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: "open" })
    this.shadow.adoptedStyleSheets = [sharedStyles]
    this.channel = new BroadcastChannel(FACETS_CHANNEL)

    const template = document.createElement("template")
    template.innerHTML = `<style>${styles}</style>${html}`
    this.shadow.appendChild(template.content.cloneNode(true))
  }

  connectedCallback() {
    const content = this.shadow.getElementById("content")!
    if (!this.resultsTable) {
      this.resultsTable = document.createElement(
        "results-table",
      ) as ResultsTable
    }
    if (this.resultsTable.parentNode !== content) {
      content.appendChild(this.resultsTable)
    }

    this.filterStatus = this.shadow.getElementById(
      "filter-status",
    ) as HTMLDivElement

    this.errorStatus = this.shadow.getElementById(
      "error-status",
    ) as HTMLDivElement

    this.datasetChooser =
      this.shadow.querySelector<DatasetChooser>("dataset-chooser")!
    this.datasetChooser.addEventListener("change", async (event) => {
      const { file, format, sourceUrl } = (event as DatasetChangeEvent).detail
      try {
        // Show "Parsing …" immediately on change, before clearing the store and
        // parsing. The chooser no longer renders a preview on change, so nothing
        // flashes a stale loaded preview between loading and parsing.
        this.datasetChooser.setParsing()
        await graphStore.clear()
        // URL loads pass the format detected by fetchRdf; file loads omit it,
        // letting graph-store detect from the file name/type.
        await graphStore.loadFile(file, format ? { format } : undefined)
        // Persist after a successful parse so OPFS never holds invalid bytes.
        await persistedData.save(file, sourceUrl, format)
        this.datasetChooser.setLoaded({
          name: file.name,
          size: file.size,
          sourceUrl,
        })
      } catch (error) {
        console.error(error)
        const raw = error instanceof Error ? error.message : String(error)
        const isParserError = /^Parser error/i.test(raw)
        if (isParserError) {
          try {
            const body = await file.text()
            console.log(
              `Source for failed parse of ${file.name}${sourceUrl ? ` (${sourceUrl})` : ""}:\n${body}`,
            )
          } catch (logErr) {
            console.error("failed to read file body for diagnostics", logErr)
          }
        }
        this.datasetChooser.setError(
          isParserError
            ? "Not valid Turtle (see browser console for details)"
            : raw,
        )
      }
    })
    this.datasetChooser.addEventListener("clear", async () => {
      try {
        await graphStore.clear()
        await persistedData.clear()
        this.datasetChooser.reset()
      } catch (e) {
        console.error(e)
      }
    })

    document.addEventListener("store-updated", this.handleStoreUpdated)
    document.addEventListener("store-cleared", this.handleStoreCleared)
    this.channel.addEventListener("message", this.handleFacetMessage)

    // Hydration is auto-started when src/graph-store.ts is first imported.
    // store-updated will fire once hydration loads the persisted file and
    // re-trigger loadAll, so init only needs to do the synchronous-ish setup.
    void this.init()
  }

  private async init() {
    await this.restorePreview()
    this.handleDatasetQueryParam()
    this.loadAll()
  }

  private handleDatasetQueryParam() {
    const params = new URLSearchParams(window.location.search)
    const datasetUrl = params.get("dataset")
    if (datasetUrl === null) return

    // Strip the param so reloads don't re-open the dialog. Done before validating
    // so a malformed value also gets cleaned up. `keep=true` opts out for
    // testing, leaving the URL intact across reloads.
    if (params.get("keep") !== "true") {
      const cleaned = new URL(window.location.href)
      cleaned.searchParams.delete("dataset")
      window.history.replaceState({}, "", cleaned.toString())
    }

    let parsed: URL
    try {
      parsed = new URL(datasetUrl)
    } catch {
      this.datasetChooser.setError(`Invalid dataset URL: ${datasetUrl}`)
      return
    }
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      this.datasetChooser.setError(`Invalid dataset URL: ${datasetUrl}`)
      return
    }

    this.datasetChooser.openUrlChooser(datasetUrl)
  }

  private async restorePreview() {
    try {
      const meta = await persistedData.getMetadata()
      if (meta) {
        this.datasetChooser.setLoaded({
          name: meta.name,
          size: meta.size,
          sourceUrl: meta.sourceUrl,
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  disconnectedCallback() {
    document.removeEventListener("store-updated", this.handleStoreUpdated)
    document.removeEventListener("store-cleared", this.handleStoreCleared)
    this.channel.removeEventListener("message", this.handleFacetMessage)
    this.channel.close()
    void this.closeCursor()
  }

  // Close and forget the current cursor, swallowing close() rejections (a
  // failed close must not surface as a load error). Returns the close promise
  // so callers can await teardown in tests.
  private closeCursor() {
    const cursor = this.cursor
    this.cursor = null
    return cursor?.close().catch(() => {})
  }

  private handleStoreUpdated = () => {
    // A fresh load drops any active facet filter.
    this.setFilterStatus(null)
    this.loadAll()
  }

  private handleStoreCleared = () => {
    this.setFilterStatus(null)
    this.setLoadError(null)
    this.controller?.abort() // cancel any in-flight loads
    this.controller = null
    void this.closeCursor()
    this.resultsTable.rowSource = null
  }

  private handleFacetMessage = (event: MessageEvent) => {
    const msg = event.data as FacetSelectionMsg
    if (msg.type !== "facet-selection") return
    if (msg.query === null) {
      this.setFilterStatus(null)
      this.loadAll()
    } else {
      this.loadFiltered(msg.query)
    }
  }

  private async loadAll() {
    this.controller?.abort()
    this.controller = new AbortController()
    const { signal } = this.controller
    this.setLoadError(null)
    void this.closeCursor()
    try {
      const prefixes = await graphStore.getNamespaces({ signal })
      this.resultsTable.prefixes = prefixes

      // size() is the exact triple count, so the all-triples table is sized
      // correctly up front. Rows come from a windowed cursor that fetches only
      // the visible slice — the whole result is never materialized into a
      // single (cap-busting) string across the worker boundary.
      const total = await graphStore.size({ signal })
      if (signal.aborted) return
      if (total === 0) {
        this.resultsTable.rowSource = null
        return
      }

      const cursor = await graphStore.openSelectCursor(ALL_TRIPLES_QUERY, {
        signal,
      })
      // A newer load may have superseded us while opening; don't leak the cursor.
      if (signal.aborted) {
        void cursor.close()
        return
      }
      this.cursor = cursor
      this.resultsTable.rowSource = cursorRowSource(cursor, total)
    } catch (error) {
      if (signal.aborted) return
      this.handleLoadError(error)
    }
  }

  private async loadFiltered(query: string) {
    this.controller?.abort()
    this.controller = new AbortController()
    const { signal } = this.controller
    this.setLoadError(null)
    // Leaving the all-triples view; free its cursor. The facet-filtered subset
    // is a CONSTRUCT result and stays materialized (it is a filtered slice, not
    // the unbounded all-triples set).
    void this.closeCursor()
    try {
      const prefixes = await graphStore.getNamespaces({ signal })
      this.resultsTable.prefixes = prefixes

      const result = await graphStore.query(query, { signal })
      if (result.type !== "construct") return
      const sel = quadsToSelectResults(result.quads)
      this.resultsTable.selectResults =
        sel.results.bindings.length === 0 ? null : sel
      this.setFilterStatus(
        `Showing ${sel.results.bindings.length} triple${sel.results.bindings.length === 1 ? "" : "s"} (filtered by facets)`,
      )
    } catch (error) {
      if (signal.aborted) return
      this.handleLoadError(error)
    }
  }

  private setFilterStatus(text: string | null) {
    if (text === null) {
      this.filterStatus.hidden = true
      this.filterStatus.textContent = ""
    } else {
      this.filterStatus.hidden = false
      this.filterStatus.textContent = text
    }
  }

  // A failed results query must never be a silent empty table: clear any stale
  // rows and show the reason. (Large results that used to overflow the
  // worker→client string cap are now streamed via openSelectCursor, but any
  // other failure still needs to be visible rather than console-only.)
  private handleLoadError(error: unknown) {
    console.error(error)
    // Setting rowSource clears any prior selectResults too, so a failed load
    // never leaves stale rows behind regardless of which path produced them.
    this.resultsTable.rowSource = null
    const message = error instanceof Error ? error.message : String(error)
    this.setLoadError(`Could not display results: ${message}`)
  }

  private setLoadError(text: string | null) {
    if (text === null) {
      this.errorStatus.hidden = true
      this.errorStatus.textContent = ""
    } else {
      this.errorStatus.hidden = false
      this.errorStatus.textContent = text
    }
  }
}

customElements.define("data-view", DataView)
export { DataView }
